import { useEffect, useState } from 'react';
import {
  ApiError,
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Page,
  api,
  brl,
  cx,
  dateOnly,
} from '@mobi/web';

type Tab = 'fares' | 'plans';

export function Finance() {
  const [tab, setTab] = useState<Tab>('fares');

  return (
    <Page title="Financeiro" subtitle="Tarifas por veículo e planos de assinatura dos motoristas.">
      <div className="mb-5 flex gap-1">
        {([
          { v: 'fares', label: 'Tarifas' },
          { v: 'plans', label: 'Planos e assinaturas' },
        ] as { v: Tab; label: string }[]).map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => setTab(t.v)}
            className={cx(
              'rounded-xl px-4 py-2 text-sm font-medium transition',
              tab === t.v ? 'bg-brand/15 text-brand' : 'bg-surface-2 text-muted',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'fares' ? <Fares /> : <Plans />}
    </Page>
  );
}

interface Fare {
  id: number;
  vehicleType: 'car' | 'moto';
  baseCents: number;
  perKmCents: number;
  perMinCents: number;
  minFareCents: number;
  active: boolean;
  createdAt: string;
  createdByName: string | null;
}

function Fares() {
  const [fares, setFares] = useState<Fare[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [vehicleType, setVehicleType] = useState<'car' | 'moto'>('car');
  const [base, setBase] = useState('');
  const [perKm, setPerKm] = useState('');
  const [perMin, setPerMin] = useState('');
  const [minFare, setMinFare] = useState('');

  async function load() {
    setFares((await api<{ fares: Fare[] }>('/api/admin/fares')).fares);
  }

  useEffect(() => { void load(); }, []);

  async function publish() {
    setBusy(true);
    setError(null);
    try {
      await api('/api/admin/fares', {
        body: {
          vehicleType,
          baseCents: Math.round(Number(base.replace(',', '.')) * 100),
          perKmCents: Math.round(Number(perKm.replace(',', '.')) * 100),
          perMinCents: Math.round(Number(perMin.replace(',', '.')) * 100),
          minFareCents: Math.round(Number(minFare.replace(',', '.')) * 100),
        },
      });
      setBase(''); setPerKm(''); setPerMin(''); setMinFare('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível publicar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <Card>
        <p className="mb-3 font-semibold">Nova tarifa</p>
        <p className="mb-3 text-sm text-faint">
          Publicar cria uma versão nova e desativa a anterior. Corrida já feita mantém o preço da época.
        </p>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="grid gap-3 sm:grid-cols-5">
          <Field label="Veículo">
            <select
              value={vehicleType}
              onChange={(e) => setVehicleType(e.target.value as 'car' | 'moto')}
              className="w-full rounded-2xl border border-line bg-surface-2 px-4 py-3.5 text-base"
            >
              <option value="car">carro</option>
              <option value="moto">moto</option>
            </select>
          </Field>
          <Field label="Base (R$)"><Input value={base} onChange={(e) => setBase(e.target.value)} inputMode="decimal" /></Field>
          <Field label="Por km (R$)"><Input value={perKm} onChange={(e) => setPerKm(e.target.value)} inputMode="decimal" /></Field>
          <Field label="Por min (R$)"><Input value={perMin} onChange={(e) => setPerMin(e.target.value)} inputMode="decimal" /></Field>
          <Field label="Mínimo (R$)"><Input value={minFare} onChange={(e) => setMinFare(e.target.value)} inputMode="decimal" /></Field>
        </div>
        <Button className="mt-4" onClick={publish} loading={busy}>Publicar</Button>
      </Card>

      <Card className="divide-y divide-line p-0">
        {fares.map((f) => (
          <div key={f.id} className="flex items-center justify-between px-5 py-4 text-sm">
            <div>
              <p className="font-medium">{f.vehicleType === 'car' ? 'Carro' : 'Moto'} {f.active && <Badge tone="good">ativa</Badge>}</p>
              <p className="text-faint">
                base {brl(f.baseCents)} · {brl(f.perKmCents)}/km · {brl(f.perMinCents)}/min · mínimo {brl(f.minFareCents)}
              </p>
            </div>
            <p className="text-faint">{dateOnly(f.createdAt)}</p>
          </div>
        ))}
      </Card>
    </div>
  );
}

interface Plan {
  id: number;
  name: string;
  days: number;
  priceCents: number;
  active: boolean;
}

function Plans() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [days, setDays] = useState('');
  const [price, setPrice] = useState('');

  const [driverId, setDriverId] = useState('');
  const [planId, setPlanId] = useState('');
  const [grantMsg, setGrantMsg] = useState<string | null>(null);

  async function load() {
    setPlans((await api<{ plans: Plan[] }>('/api/admin/plans')).plans);
  }

  useEffect(() => { void load(); }, []);

  async function createPlan() {
    setBusy(true);
    setError(null);
    try {
      await api('/api/admin/plans', { body: { name, days: Number(days), priceCents: Math.round(Number(price.replace(',', '.')) * 100) } });
      setName(''); setDays(''); setPrice('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível criar');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(id: number) {
    await api(`/api/admin/plans/${id}/toggle`, { method: 'POST' });
    await load();
  }

  async function grant() {
    setBusy(true);
    setError(null);
    setGrantMsg(null);
    try {
      const res = await api<{ planName: string; expiresOn: string }>(
        `/api/admin/drivers/${driverId}/subscription`,
        { body: { planId: Number(planId) } },
      );
      setGrantMsg(`${res.planName} registrado — vence em ${dateOnly(res.expiresOn)}`);
      setDriverId('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível registrar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <Card>
        <p className="mb-3 font-semibold">Novo plano</p>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Nome"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Dias"><Input value={days} onChange={(e) => setDays(e.target.value)} inputMode="numeric" /></Field>
          <Field label="Preço (R$)"><Input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" /></Field>
        </div>
        <Button className="mt-4" onClick={createPlan} loading={busy}>Criar plano</Button>
      </Card>

      <Card className="divide-y divide-line p-0">
        {plans.map((p) => (
          <div key={p.id} className="flex items-center justify-between px-5 py-4 text-sm">
            <div>
              <p className="font-medium">{p.name} {!p.active && <Badge tone="neutral">inativo</Badge>}</p>
              <p className="text-faint">{p.days} dias · {brl(p.priceCents)}</p>
            </div>
            <Button variant="outline" onClick={() => toggle(p.id)}>{p.active ? 'Desativar' : 'Ativar'}</Button>
          </div>
        ))}
      </Card>

      <Card>
        <p className="mb-3 font-semibold">Registrar pagamento de assinatura</p>
        <p className="mb-3 text-sm text-faint">Renovação emenda no vencimento vigente — quem paga adiantado não perde dias.</p>
        {grantMsg && <p className="mb-3 text-sm text-good">{grantMsg}</p>}
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="ID do motorista"><Input value={driverId} onChange={(e) => setDriverId(e.target.value)} inputMode="numeric" /></Field>
          <Field label="Plano">
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="w-full rounded-2xl border border-line bg-surface-2 px-4 py-3.5 text-base"
            >
              <option value="">selecione</option>
              {plans.filter((p) => p.active).map((p) => (
                <option key={p.id} value={p.id}>{p.name} · {brl(p.priceCents)}</option>
              ))}
            </select>
          </Field>
        </div>
        <Button className="mt-4" onClick={grant} loading={busy} disabled={!driverId || !planId}>
          Registrar pagamento
        </Button>
      </Card>
    </div>
  );
}
